import { Component, Vue, Prop, Provide, Emit, Watch, Inject } from 'vue-property-decorator';
import { PropOptions } from 'vue';
import CreditCardContainer from './CreditCardContainer.vue';
import InvoiceContainer from './InvoiceContainer.vue';
import AchContainer from './AchContainer.vue';
import { default as InvoiceContainerClass } from '@/components/payment-container/InvoiceContainerVue';
import { default as CreditCardContainerClass } from '@/components/payment-container/CreditCardContainerVue';
import { default as AchContainerClass } from '@/components/payment-container/AchContainerVue';
import { createPaymentProcessorOptions, PaymentProcessorOptions, PaymentContainerToken, createPaymentContainerToken, InvoiceTokenData, CreditCardTokenData, PaymentTypeEnum, AchTokenData, AutoPayOption, ApiErrorResponse, PaymentPlan, PaymentAddressData, createPaymentAddressData, GoogleAddressComponentNameMap, createGoogleAddressComponentNameMap, GoogleAddressComponentValueMap, createGoogleAddressComponentValueMap, PaymentProcessorEnum, PaymentLimits } from '@/common/types/AllTypesExports';
import EventBus from '../../common/services/EventBus';
import { ApiPaymentErrorResponse } from '@/common/types/Payment/ApiPaymentErrorResponse';
import { PaymentProcessingStatus } from '@/common/types/Payment/PaymentProcessingStatus';
import ErrorAlert from '../../components/errors/ErrorAlert.vue';
import Validation from '../../common/services/ValidationService';
import { paymentsRepo } from '../../common/repositories/PaymentsRepo';
import { DtoCurrentUser, createDtoCurrentUser } from '@/common/types/user/DtoCurrentUser';
import { AxiosResponse } from 'axios';
import { customerRepo } from '../../common/repositories/CustomerRepo';
import FieldValidationErrors from '../../components/errors/FieldValidationErrors.vue';
import { INoviNsPaymentGlobals } from '../../common/types/Payment/NoviNsPaymentGlobals';
import { default as CreditCardEntryFormClass } from './CreditCardEntryFormVue';
import { default as FiservAchEntryFormClass } from './FiservAchEntryFormVue';
import { AdminPaymentLimitsMessages, PaymentLimitsMessages } from '../../common/types/Payment/PaymentLimitsMessages';
import { createPaymentLimits, THEORETICAL_MAX_PAYMENT } from '../../common/types/Payment/PaymentLimits';
import { fillRecaptchaToken } from "../../common/types/recaptcha/RecaptchaEnterprise"

declare let noviNs: INoviNsPaymentGlobals;

@Component({
    name: 'PaymentContainer',
    components: {
        CreditCardContainer,
        InvoiceContainer,
        AchContainer,
        ErrorAlert,
        FieldValidationErrors
    }
})
export default class PaymentContainer extends Vue {
    @Prop({ required: true, default: () => createPaymentProcessorOptions() } as PropOptions<PaymentProcessorOptions>)
    processorOptions!: PaymentProcessorOptions;

    @Prop({ required: false, default: false })
    disabled!: boolean;

    @Prop({ required: false, default: false })
    autoPayRequired!: boolean;

    @Prop({ required: false, default: () => 0 })
    duesPayerId!: number;

    @Prop({ required: false, default: undefined })
    initialErrorMessage: string | undefined;

    @Prop({ required: false, default: () => 0 })
    proposedDonateTransactionFeeAmount: number = 0;

    @Prop({ required: false, default: () => 0 })
    eligibleForPaymentPlanAmount: number = 0;

    @Prop({ required: false, default: () => [] })
    eligibleItemsForPaymentPlan: [] = [];

    @Prop({ required: true, default: false })
    setMemberTypeDefaultBillableParty: boolean | undefined;

    @Provide('user')
    currentUser: DtoCurrentUser = createDtoCurrentUser();

    isDonatingTransactionFee: boolean = false;

    @Inject('validator')
    $validator: any;

    /* This is used to determine if a user has logged in during the course of MemberRegistration.
    Normally we can check a user's logged in status from the PaymentContainer, but when the user is coming from MemberRegistration,
    PaymentContainer is a grandchild of MemberRegistration which is the component that is determining and updating the logged in status. 
    However, the PaymentContainer is not always a nested component of MemberRegistration, so this property is optional */
    @Inject('userLoggedInOnMemberRegistration') readonly userLoggedInGetter?: () => boolean;

    addressAutocomplete!: google.maps.places.Autocomplete;
    addressAutocompleteListener!: google.maps.MapsEventListener;
    mapsIsInitialized = false;

    // This object allows us to map the incoming google result keys
    addressComponentNamesMap: GoogleAddressComponentNameMap = createGoogleAddressComponentNameMap();

    // This object allows us to map the incoming google result values
    addressComponentValues: GoogleAddressComponentValueMap = createGoogleAddressComponentValueMap();

    creditCardActive = false;
    invoiceActive = false;
    paymentPlansActive = false;
    achActive = false;
    paymentContainerToken: PaymentContainerToken = createPaymentContainerToken(<PaymentContainerToken>{ paymentType: this.paymentType });
    addressData: PaymentAddressData = createPaymentAddressData();
    isProcessing = true;
    hasError = false;
    preventCreditCard = false;
    creditCardLocked = false;
    cardRestrictionMessage = "";
    invoiceLocked = false;
    preventAch = false;
    achLocked = false;
    errorMessage = '';
    globalErrorMessage: string = '';
    total: number | null = this.processorOptions.initialTotal;
    guestName: string = this.processorOptions.initialGuestName;
    guestCompany: string = this.processorOptions.initialGuestCompany;
    forceSaveCard = false;
    forceSaveAch = false;
    hasItemWithDisabledInvoicing = false;
    disabledInvoiceTitle = '';
    autoPayOptions: AutoPayOption[] = [];
    autoPayOptionsPulled = false;
    billablePartiesNotLoadedErrorMessage = 'Please select a Bill To party for this transaction.';
    paymentPlansNotLoadedErrorMessage = 'Please select a Payment Plan for this transaction.';
    userLoaded = false;
    isPayingSubscriptionInvoice: boolean = this.processorOptions.isPayingSubscriptionInvoice;
    paymentPlans: PaymentPlan[] = [];
    showShippingAddress = false;
    isCopyFromAboveSelected = false;
    newCardSelected = false;
    newACHSelected = false;
    paymentLimits: PaymentLimits = createPaymentLimits();
    hasPaymentLimitViolation = false;
    paymentLimitMessage: string = '';
    adminPaymentLimitMessage: string = '';
    ecommerceDeliveryAddressProvidedOrRequired = false;
    hasNextStep = false;

    private currentPaymentType: PaymentTypeEnum = PaymentTypeEnum.creditCard;
    private localEnableInvoice: boolean = true;

    get free() {
        return this.total === null || this.total === 0;
    }

    get showTabs() {
        let numberOfTabs = 0;
        if (this.processorOptions.enableInvoice) {
            numberOfTabs++;
        }
        if (this.processorOptions.enableCreditCard) {
            numberOfTabs++;
        }
        if (this.processorOptions.enableACH) {
            numberOfTabs++;
        }
        if (this.paymentPlans.length > 0) {
            numberOfTabs++;
        }

        return numberOfTabs > 1;
    }

    @Watch("processorOptions.enableACH")
    updatePaymentType() {
        if (this.processorOptions.enableACH) {
            this.toggleTabActivation(PaymentTypeEnum.ach);
        }
        else if (this.processorOptions.enableCreditCard) {
            this.toggleTabActivation(PaymentTypeEnum.creditCard);
        }
    }

    @Watch('paymentLimits', { deep: true })
    onPaymentLimitsChanged() {
        this.evaluatePaymentLimits();
    }

    @Watch('total')
    onTotalChanged() {
        this.evaluatePaymentLimits();
    }

    get paymentType(): PaymentTypeEnum {
        if (this.currentPaymentType)
            return this.currentPaymentType;

        if (this.invoiceActive) {
            return PaymentTypeEnum.invoice;
        }
        else if (this.achActive) {
            return PaymentTypeEnum.ach;
        }
        else {
            return PaymentTypeEnum.creditCard;
        }
    }

    set paymentType(value: PaymentTypeEnum) {
        this.currentPaymentType = value;
    }

    get isInvoiceEnabled() {
        return this.processorOptions.enableInvoice && this.localEnableInvoice;
    }

    get convenienceFee() {
        return !this.free && this.invoiceActive && !this.paymentContainerToken.invoiceTokenData.waiveInvoiceFee ? this.processorOptions.invoiceFee : 0;
    }

    get paymentPlanMinimumAmount() {
        return noviNs.paymentPlanMinimumAmount;
    }

    get showSavedCards() {
        if (this.processorOptions && this.processorOptions.savedCards) {
            return this.processorOptions.enableSaveCreditCard && this.processorOptions.savedCards.length > 0;
        }
        return false;
    }

    get showSavedACH() {
        if (this.processorOptions && this.processorOptions.savedAchs) {
            return this.processorOptions.enableSaveAch && this.processorOptions.savedAchs.length > 0;
        }
        return false;
    }

    get isSubmitDisabled() {
        if (this.invoiceActive && this.showWarningIcon) {
            return true;
        }

        if (this.hasNextStep) {
            return true;
        }

        if (this.disabled)
            return true;

        if (this.isProcessing)
            return true;

        if (this.processorOptions.paymentsThrottled && this.paymentType == PaymentTypeEnum.creditCard)
            return true;

        if (this.processorOptions.showAdminAchDisabledMessage && this.paymentType == PaymentTypeEnum.ach)
            return true;

        if (this.total && this.total > 0) {
            if (this.paymentType == PaymentTypeEnum.creditCard && (!this.processorOptions.enableCreditCard || this.creditCardLocked || this.cardRestrictionMessage || this.preventCreditCard))
                return true;

            if (this.paymentType == PaymentTypeEnum.ach && this.preventAch)
                return true;

            if (this.paymentType == PaymentTypeEnum.invoice && (!this.isInvoiceEnabled || this.invoiceLocked))
                return true;

            if (this.hasPaymentLimitViolation)
                return true;
        }

        if (this.total == null)
            return true;

        // This is a special check for donations inputs on the checkout that could be in error but are not being propagated to the container. 
        // To accomidate this we will do a check if there are any errors with donation inputs at this scope and lock the button if so. 
        if (this.errors.items.filter(x => x.field != null && x.field.includes("_amount")).length > 0)
            return true;

        if (this.invoiceActive && (this.hasItemWithDisabledInvoicing || this.processorOptions.invoiceWarningMessage.includes("Please purchase separately") || this.processorOptions.invoiceWarningMessage.includes("You must login")))
            return true;

        if (this.paymentType == PaymentTypeEnum.ach && this.isLoggedIn == false)
            return true;

        return false;
    }

    get processingText() {
        return this.processorOptions.submitButtonSavingText
            ? this.processorOptions.submitButtonSavingText
            : 'Processing...';
    }

    get submitButtonText() {
        return this.processorOptions.submitButtonText
            ? this.processorOptions.submitButtonText
            : 'Complete Registration';
    }

    get disabledText() {
        if (this.isProcessing)
            return this.processingText;
        else if (this.creditCardLocked || this.invoiceLocked)
            return this.submitButtonText;
        else if (this.processorOptions.disabledButtonText)
            return this.processorOptions.disabledButtonText;
        else
            return this.submitButtonText;
    }

    get showWarningIcon(): boolean {
        return this.hasItemWithDisabledInvoicing ||
            (this.processorOptions.invoiceWarningMessage.includes("Please purchase separately") ?? false) ||
            (this.processorOptions.invoiceWarningMessage.includes("You must login") ?? false);
    }

    get isUserLoggedInFromMemberRegistration() {
        return this.userLoggedInGetter ? this.userLoggedInGetter() : false;
    }

    get isLoggedIn(): boolean {
        if (this.isUserLoggedInFromMemberRegistration) {
            this.currentUser.isLoggedIn = true; // this value is used the AchContainer to disable ACH for guest users
            return true;
        }
        return this.currentUser.isLoggedIn;
    }

    get showLoginRequired(): boolean {
        return this.processorOptions.invoiceRequiresLogin && !this.isLoggedIn;
    }


    get showShippingAddressDetails(): boolean {
        const baseVisibility = noviNs.automatedSalesTaxEnabled
            && !noviNs.UseEventAddressForSalesTax
            && (!this.processorOptions.enableAdminOptions || !this.achActive)
            && ((this.showShippingAddress && (this.isLoggedIn || this.creditCardActive)) || (noviNs.automatedSalesTaxEnabled && ((!this.isLoggedIn) || noviNs.customSalesReceipt) && this.creditCardActive))
            && !this.showPaymentLimitAlert; // hide shipping input when the user cannot proceed with checkout because their total is not within the allowed payment limits

        if (!baseVisibility) {
            return false;
        }

        if (!noviNs.ecommerceCheckout) {
            return true;
        }

        return !this.ecommerceDeliveryAddressProvidedOrRequired;
    }

    @Watch("showShippingAddressDetails", { immediate: true })

    onShowShippingAddressDetailsUpdate() {
        if (!this.mapsIsInitialized) {
            this.setupGoogleAutocomplete();
        }
    }

    login() {
        window.location.href = "/login?returnUrl=" + window.location.pathname;
    }

    get qbPaymentProcessor(): boolean {
        return noviNs.paymentProcessor == PaymentProcessorEnum.quickBooksPayments;
    }

    get showBillToLabel(): boolean {
        return this.qbPaymentProcessor && !this.isLoggedIn && !noviNs.frontendEcommerceCheckout && this.creditCardActive;
    }

    get showCopyAbove() {
        if (noviNs.paymentProcessor == PaymentProcessorEnum.quickBooksPayments) {
            return this.creditCardActive && (!this.showSavedCards || this.newCardSelected || !this.isLoggedIn);
        }

        if (noviNs.paymentProcessor == PaymentProcessorEnum.fiserv) {
            if (this.creditCardActive) {
                return !this.showSavedCards || this.newCardSelected || !this.isLoggedIn;
            }

            if (this.achActive) {
                return !this.showSavedACH || this.newACHSelected || !this.isLoggedIn;
            }
        }

        return false;
    }

    // Show error for ACH and credit card when the total is over the respective max limit allowed by the association
    // This should take into account any variables being used in the PaymentContainer.vue file to hide or show the AchContainer or CreditCardContainer
    get showPaymentLimitAlert(): boolean {
        return (this.achBlockedByPaymentLimit || this.creditCardBlockedByPaymentLimit);
    }

    // Blocks the credit card UI when the current total exceeds allowed payment limits. 
    // This is specific to the credit card conatiner - this does not prevent payment plan credit card container from loading
    get creditCardBlockedByPaymentLimit(): boolean {
        return this.processorOptions.enableCreditCard && this.creditCardActive && this.hasPaymentLimitViolation;
    }

    // Blocks the ACH UI when the transaction total violates ACH payment limits.
    get achBlockedByPaymentLimit(): boolean {
        return this.processorOptions.enableACH && !this.processorOptions.showAdminAchDisabledMessage && this.achActive && this.hasPaymentLimitViolation;
    }

    onSubmit() {
        var validationPassed = false;

        this.$validator.validateAll()
            .then((valid: any) => {
                if (valid && !this.errors.any()) {
                    validationPassed = true;
                    this.onErrorToggle(false, '');
                }
                else {

                    if (!this.errors.any()) {
                        validationPassed = true;
                        this.onErrorToggle(false, '');
                    }
                    else {
                        validationPassed = false;
                        this.onErrorToggle(true, 'Please correct the errors shown before submitting.');
                    }
                }

                if (this.processorOptions.paymentsThrottled && this.paymentType == PaymentTypeEnum.creditCard) {
                    validationPassed = false;
                    this.onErrorToggle(true, "");
                }

                if (this.currentPaymentType == PaymentTypeEnum.creditCard || this.currentPaymentType == PaymentTypeEnum.paymentPlan) {
                    let creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;

                    if (this.currentPaymentType == PaymentTypeEnum.creditCard && this.creditCardBlockedByPaymentLimit) {
                        validationPassed = false;
                        var errorMessage = this.processorOptions.enableAdminOptions ? this.adminPaymentLimitMessage : this.paymentLimitMessage;
                        this.onErrorToggle(true, errorMessage);
                    }

                    if (this.total && this.total > 0 && creditCardContainer.isCheckingForBillableParties) {
                        validationPassed = false;
                        this.onErrorToggle(true, this.billablePartiesNotLoadedErrorMessage);
                    }

                    if (this.total && this.total > 0 && this.currentPaymentType == PaymentTypeEnum.paymentPlan && creditCardContainer.isCheckingForPaymentPlans) {
                        validationPassed = false;
                        this.onErrorToggle(true, this.paymentPlansNotLoadedErrorMessage);
                    }

                    if (this.total && this.total > 0 && this.paymentPlansActive && creditCardContainer.currentPlanId == 0) {
                        validationPassed = false;
                        this.onErrorToggle(true, "You must selected payment plan option to continue.");
                    }

                    if (this.total && this.total > 0 && this.paymentPlansActive && !creditCardContainer.acceptPaymentPlanTerms) {
                        validationPassed = false;
                        this.onErrorToggle(true, "You must accept payment plan conditions to continue.");
                    }
                }
                else if (this.currentPaymentType == PaymentTypeEnum.ach) {
                    let achContainer = <AchContainerClass>this.$refs.achContainer;

                    if (this.achBlockedByPaymentLimit) {
                        validationPassed = false;
                        var errorMessage = this.processorOptions.enableAdminOptions ? this.adminPaymentLimitMessage : this.paymentLimitMessage;
                        this.onErrorToggle(true, errorMessage);
                    }

                    if (this.processorOptions.showAdminAchDisabledMessage) {
                        validationPassed = false;
                        this.onErrorToggle(true, "ACH is not available to admins.");
                    }
                    else if (this.total && this.total > 0 && achContainer.isCheckingForBillableParties) {
                        validationPassed = false;
                        this.onErrorToggle(true, this.billablePartiesNotLoadedErrorMessage);
                    }
                }
                else if (this.currentPaymentType == PaymentTypeEnum.invoice) {
                    let invoiceContainer = <InvoiceContainerClass>this.$refs.invoice;

                    if (this.total && this.total > 0 && invoiceContainer.isCheckingForBillableParties) {
                        validationPassed = false;
                        this.onErrorToggle(true, this.billablePartiesNotLoadedErrorMessage);
                    }
                }
            })
            .then(() => {
                if (!validationPassed)
                    return;

                this.paymentContainerToken.paymentType = this.free ? PaymentTypeEnum.free : this.paymentType;

                const input = document.querySelector<HTMLInputElement>('input[name="g-recaptcha-response"]');
                const siteKey = (window as any).__recaptchaEnterpriseSiteKey;

                if (input && siteKey) {
                    // Enterprise reCAPTCHA runs before payment tokenization or form submit.
                    // Google may show a visual challenge; if the user dismisses it (e.g. clicks outside),
                    // execute fails and we get an empty token. Do not show "processing" or continue checkout
                    // in that case — same behavior as recaptchaEnterprise.js on standard MVC forms.
                    fillRecaptchaToken(siteKey, (token) => {
                        input.value = token || '';

                        if (!token) {
                            // User dismissed the challenge or verification did not complete — stop quietly.
                            return;
                        }

                        // Only after a successful token: show processing and run card/ACH tokenization or emit tokenReady.
                        this.onProcessingUpdated(true);
                        this.processPaymentByPaymentType();
                    }, noviNs.recaptchaEnterpriseAction);
                }
                else {
                    // No enterprise reCAPTCHA on this page — proceed with the existing submit path.
                    this.onProcessingUpdated(true);
                    this.processPaymentByPaymentType();
                }
            });
    }

    processPaymentByPaymentType() {
        if (this.paymentType === PaymentTypeEnum.invoice || this.free) {
            this.tokenReady();
        }
        else if (this.paymentType == PaymentTypeEnum.creditCard || this.currentPaymentType == PaymentTypeEnum.paymentPlan) {
            const creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
            creditCardContainer.createPaymentToken();
        }
        else if (this.paymentType == PaymentTypeEnum.ach) {
            const achContainer = <AchContainerClass>this.$refs.achContainer;
            achContainer.createPaymentToken();
        }
    }

    onErrorToggle(hasError: boolean, errorMsg: string) {
        if (!hasError) {
            this.hasError = false;
            this.errorMessage = '';
        }
        else {
            this.hasError = true;
            this.onProcessingUpdated(false);
            this.errorMessage = errorMsg ? errorMsg : 'An unspecified error has occurred. Please retry your current action. If this problem persists, please contact support.';
        }
    }

    handleGeneralError(message: string) {
        this.onErrorToggle(true, message);
    }

    onAllowSubmitToggle(allowSubmit: boolean) {
        this.creditCardLocked = !allowSubmit;
        this.invoiceLocked = !allowSubmit;
    }

    onForceSaveCardUpdated(forceSaveCard: boolean) {
        this.forceSaveCard = forceSaveCard;
    }

    onForceSaveAchUpdated(forceSaveAch: boolean) {
        this.forceSaveAch = forceSaveAch;
    }

    onCreditCardRestrictionUpdated(cardRestrictionMessage: string) {
        this.cardRestrictionMessage = cardRestrictionMessage;
    }

    onInvoiceWarningUpdated(message: string) {
        this.processorOptions.invoiceWarningMessage = message;
    }

    onTotalUpdated(total: number) {
        this.total = total;
    }

    onProposedDonateTransactionFeeAmountUpdated(value: number) {
        this.proposedDonateTransactionFeeAmount = value;
    }

    onEligibleForPaymentPlanAmountUpdated(value: number) {
        this.eligibleForPaymentPlanAmount = value;

        if (this.eligibleForPaymentPlanAmount < this.paymentPlanMinimumAmount && this.paymentPlansActive) {
            this.paymentPlansActive = false;
            this.creditCardActive = true;
        }
    }

    @Watch("eligibleItemsForPaymentPlan", { immediate: true })
    onEligibleItemsForPaymentPlanUpdated(value: []) {
        this.eligibleItemsForPaymentPlan = value;

        if (this.eligibleItemsForPaymentPlan.length == 0 && this.paymentPlansActive) {
            this.paymentPlansActive = false;
            this.creditCardActive = true;
        }
        else if (this.eligibleItemsForPaymentPlan?.length > 0 && this.eligibleForPaymentPlanAmount >= this.paymentPlanMinimumAmount && !this.paymentPlansActive) {
            const creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
            if (creditCardContainer) {
                creditCardContainer.loadPaymentPlans(this.eligibleItemsForPaymentPlan);
            }
            else {
                this.loadPaymentPlans(this.eligibleItemsForPaymentPlan);
            }
        }
    }

    onEnableAutoPayUpdated(enableAutoPay: boolean) {
        this.processorOptions.enableAutoPay = enableAutoPay;
    }

    onProcessingUpdated(processing: boolean) {
        this.isProcessing = processing;
        this.$emit("update:is-processing", this.isProcessing);
    }

    onCreditCardBillingStateUpdate(state: string) {
        this.$emit("update:credit-card-billing-state", state);
    }

    onIsDonatingTransactionFeeUpdated(value: boolean) {
        this.$emit("update-is-donating-transaction-fee", value);
        this.isDonatingTransactionFee = value;
    }

    onNewCardSelectedUpdated(value: boolean) {
        this.newCardSelected = value && (this.paymentType == PaymentTypeEnum.creditCard || this.paymentType == PaymentTypeEnum.paymentPlan);
    }

    onNewACHSelectedUpdated(value: boolean) {
        this.newACHSelected = value && this.paymentType == PaymentTypeEnum.ach;
    }

    onPurchaserChanged(value: string) {
        this.processorOptions.billablePartyId = parseInt(value, 10);
    }

    @Watch('convenienceFee')
    onConvenienceFeeChanged(newConvenienceFee: boolean) {
        this.$emit("update:convenience-fee", newConvenienceFee);
    }

    @Watch('autoPayOptionsEnabled')
    onAutoPayOptionsUpdated() {
        if (noviNs.autoPayInstantOnOptIn) {
            this.$emit("update:auto-pay-options", this.autoPayOptions);
        }
    }

    get autoPayOptionsEnabled() {
        return this.autoPayOptions.map(o => o.enabled)
    }

    @Watch('paymentType')
    onPaymentTypeChanged(newPaymentType: PaymentTypeEnum) {
        this.$emit("update:payment-type", newPaymentType.toString());
        this.notifyKoPaymentType(newPaymentType);
        this.$nextTick(() => {
            if (newPaymentType.toString() == 'ACH') {
                const achContainer = <AchContainerClass>this.$refs.achContainer;
                if (achContainer) {
                    if (this.guestName) {
                        achContainer.onGuestDataUpdated(this.guestName, true);
                    }
                    if (this.guestCompany) {
                        achContainer.onGuestDataUpdated(this.guestCompany, false);
                    }
                }
            }
            if (newPaymentType.toString() == 'credit-card') {
                const creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
                if (creditCardContainer) {
                    if (this.guestName) {
                        creditCardContainer.onGuestDataUpdated(this.guestName, true);
                    }
                    if (this.guestCompany) {
                        creditCardContainer.onGuestDataUpdated(this.guestCompany, false);
                    }
                }
            }
            this.evaluatePaymentLimits();
        });
    }

    @Watch('isCopyFromAboveSelected')
    onIsCopyFromAboveSelectedChanged(selected: boolean) {
        if (selected) {
            if (this.creditCardActive || this.paymentPlansActive) {
                const creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
                if (creditCardContainer) {
                    let creditCardEntryFormContainer = <CreditCardEntryFormClass>creditCardContainer.$refs.creditCardEntryForm;
                    if (creditCardEntryFormContainer) {
                        if (this.addressData && creditCardEntryFormContainer.creditCardData) {
                            this.addressData.shippingStreet = creditCardEntryFormContainer.creditCardData.billingStreet;
                            this.addressData.shippingCity = creditCardEntryFormContainer.creditCardData.billingCity;
                            this.addressData.shippingState = creditCardEntryFormContainer.creditCardData.billingState;
                            this.addressData.shippingZip = creditCardEntryFormContainer.creditCardData.billingZip;
                            this.addressData.shippingCountry = creditCardEntryFormContainer.creditCardData.billingCountry;
                        }
                    }
                }
            } else if (this.achActive) {
                const achContainer = <AchContainerClass>this.$refs.achContainer;
                let achntryFormContainer = <FiservAchEntryFormClass>achContainer.$refs.achFiservEmbed;
                if (achntryFormContainer) {
                    if (this.addressData && achntryFormContainer.achData) {
                        this.addressData.shippingStreet = achntryFormContainer.achData.billingStreet;
                        this.addressData.shippingCity = achntryFormContainer.achData.billingCity;
                        this.addressData.shippingState = achntryFormContainer.achData.billingState;
                        this.addressData.shippingZip = achntryFormContainer.achData.billingZip;
                        this.addressData.shippingCountry = achntryFormContainer.achData.billingCountry;
                    }
                }
            }
        }
    }

    @Watch('errorMessage')
    onErrorMessageChanged(newErrorMessage: boolean) {
        this.$emit("update:error-message", newErrorMessage);
    }

    @Watch('free')
    onFreeChange(newFree: boolean) {
        if (newFree) {
            //If we are going free, keep the currently selected non-invoice payment method so temporary total=0 states do not reset ACH back to credit card

            if (this.paymentPlansActive) {
                this.toggleTabActivation(PaymentTypeEnum.paymentPlan);
            }
            else if (this.achActive) {
                this.toggleTabActivation(PaymentTypeEnum.ach);
            }
            else if (this.creditCardActive) {
                this.toggleTabActivation(PaymentTypeEnum.creditCard);
            }
            else if (this.processorOptions.enableCreditCard) {
                this.toggleTabActivation(PaymentTypeEnum.creditCard);
            }
            else if (this.processorOptions.enableACH) {
                this.toggleTabActivation(PaymentTypeEnum.ach);
            }
        }
    }

    notifyKoPaymentType(pt: PaymentTypeEnum) {
        const w: any = window as any;
        if (typeof w.updatePaymentType !== 'function') return;

        let val = 'credit-card';
        switch (pt) {
            case PaymentTypeEnum.ach:
                val = 'ach';
                break;
            case PaymentTypeEnum.paymentPlan:
                val = 'payment-plans';
                break;
            case PaymentTypeEnum.invoice:
                val = 'invoice';
                break;
            case PaymentTypeEnum.free:
                val = 'free';
                break;
            default:
                val = 'credit-card';
                break;
        }

        w.updatePaymentType(val);
    }

    handleApiPaymentError(paymentResponse: ApiPaymentErrorResponse) {
        this.onErrorToggle(true, paymentResponse.message);
        switch (paymentResponse.paymentProcessingStatus) {
            case PaymentProcessingStatus.Success:
            case PaymentProcessingStatus.Error:
                //Success: Payment was successful, but we caught an error so something else went wrong and we can't retry
                //Error: Payment had an error, we don't know the status and we can't retry
                this.creditCardLocked = true;
                this.invoiceLocked = true;
                break;
            case PaymentProcessingStatus.Declined:
                //Declined: Payment was declined/not processed for the given reason, need to fix and retry
                this.resetIdempotencyKey();
                break;
            case PaymentProcessingStatus.NeedsAction:
                //NeedsAction: Payment requires additional action, do not retry automatically
                // Admin payment forms (enableAdminOptions) do not support the 3DS challenge flow.
                if (this.processorOptions.enableAdminOptions) {
                    this.onErrorToggle(true, "This card requires additional verification that isn't available here. Please try another payment method.");
                    this.resetIdempotencyKey();
                    break;
                }
                this.hasNextStep = true;
                this.onErrorToggle(false, '');
                const creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
                this.isProcessing = true;
                creditCardContainer.handleNextAction(paymentResponse.nextStepToken);
                break;
            case PaymentProcessingStatus.None:
                //None: We hit an error before payment was attempted - fully safe to retry                
                break;
        }
    }

    onInvoiceAllowedUpdated(invoiceAllowed: boolean) {
        this.localEnableInvoice = invoiceAllowed;
    }

    onIsCreditCardProcessingPreventedUpdated(value: boolean) {
        this.preventCreditCard = value;
    }

    onIsAchProcessingPreventedUpdated(value: boolean) {
        this.preventAch = value;
    }

    onInvoiceTokenUpdated(invoiceToken: InvoiceTokenData) {
        var taxableChanged = invoiceToken.taxable != this.paymentContainerToken.invoiceTokenData.taxable;
        //console.log(`Invoice Token Updated: (Billable Party: ${invoiceToken.billablePartyId}) (Sales Term: ${invoiceToken.salesTermId}) (Waive Invoice Fee: ${invoiceToken.waiveInvoiceFee})`);
        Object.assign(this.paymentContainerToken.invoiceTokenData, invoiceToken);

        if (taxableChanged) {
            this.$emit("update:billable-party-taxable", invoiceToken.taxable);
        }

        this.showShippingAddress = !invoiceToken.hasPaymentAddress;
    }

    onCreditCardTokenUpdated(creditCardToken: CreditCardTokenData) {
        Object.assign(this.paymentContainerToken.creditCardTokenData, creditCardToken);
        this.showShippingAddress = !creditCardToken.hasPaymentAddress;
    }

    onAchTokenUpdated(achToken: AchTokenData) {
        Object.assign(this.paymentContainerToken.achTokenData, achToken);
        this.showShippingAddress = !achToken.hasPaymentAddress;
    }

    onPaymentPlansActiveUpdated(value: boolean) {
        this.paymentPlansActive = value;
    }

    onPaymentPlansUpdated(value: []) {
        this.paymentPlans = value;
    }


    onCreditCardPaymentTokenReady(cardToken: CreditCardTokenData) {
        //console.log("Credit card token received by payment container: " + cardToken.cardToken);
        this.paymentContainerToken.paymentType = PaymentTypeEnum.creditCard;

        // Post-3DS: only attach the PaymentIntent id. Do not Object.assign the Stripe form token —
        // for saved cards it lacks paymentProcessorModule/storedCardId and would wipe them.
        if (cardToken.completedTransactionId) {
            this.paymentContainerToken.creditCardTokenData.completedTransactionId = cardToken.completedTransactionId;
            this.paymentContainerToken.creditCardTokenData.confirmationToken = '';
            this.tokenReadyAfterFreshRecaptcha();
            return;
        }

        // If the credit card details changed since the last submission, then we need to send a new idempotency key
        // Note: this is really just for stripe, which returns an error if you re-submit a payment with the same idempotency key but different card details
        // QB Payments ignores the payment details if you re-submit with the same idempotency key and returns whatever response it gave the last time
        if (this.paymentContainerToken.creditCardTokenData.storedCardId != cardToken.storedCardId
            || this.paymentContainerToken.creditCardTokenData.cardToken != cardToken.cardToken
            || this.paymentContainerToken.creditCardTokenData.confirmationToken != cardToken.confirmationToken) {
            this.resetIdempotencyKey();
        }

        Object.assign(this.paymentContainerToken.creditCardTokenData, cardToken);
        this.tokenReady();
    }

    /**
     * Enterprise tokens are single-use. Re-execute before emitting payment-token-ready again (e.g. post-3DS).
     */
    tokenReadyAfterFreshRecaptcha() {
        const input = document.querySelector<HTMLInputElement>('input[name="g-recaptcha-response"]');
        const siteKey = (window as any).__recaptchaEnterpriseSiteKey;

        if (input && siteKey) {
            input.value = '';
            fillRecaptchaToken(siteKey, (token) => {
                input.value = token || '';

                if (!token) {
                    this.onProcessingUpdated(false);
                    return;
                }

                this.tokenReady();
            }, noviNs.recaptchaEnterpriseAction);
        } else {
            this.tokenReady();
        }
    }

    onAchPaymentTokenReady(cardToken: AchTokenData) {
        this.paymentContainerToken.paymentType = PaymentTypeEnum.ach;

        Object.assign(this.paymentContainerToken.achTokenData, cardToken);
        this.tokenReady();
    }

    toggleTabActivation(paymentType: PaymentTypeEnum) {
        this.creditCardActive = paymentType === PaymentTypeEnum.creditCard || paymentType == PaymentTypeEnum.free;
        this.invoiceActive = paymentType === PaymentTypeEnum.invoice;
        this.achActive = paymentType === PaymentTypeEnum.ach;
        this.paymentPlansActive = paymentType == PaymentTypeEnum.paymentPlan;
        this.paymentType = paymentType;
    }

    setupGoogleAutocomplete() {
        if (typeof google === 'undefined') {
            return;
        }

        const elemId = "shippingStreet";
        const addressElem = document.getElementById(elemId);

        if (!addressElem) {
            return;
        }

        addressElem.addEventListener("focus", function () {
            addressElem.setAttribute("autocomplete", "nope");
        });

        this.addressAutocomplete = new google.maps.places.Autocomplete(<HTMLInputElement>addressElem, {
            types: ["geocode"],
            fields: ["address_components"],
        });
        this.addressAutocompleteListener = this.addressAutocomplete.addListener("place_changed", () => {
            const place = this.addressAutocomplete.getPlace();

            if (!place || !place.address_components) {
                return;
            }

            // Reset the current values to ensure no stale data remains
            this.addressComponentValues = createGoogleAddressComponentValueMap();

            // Map components directly
            place.address_components.forEach(addressComponent => {
                addressComponent.types.forEach(type => {
                    switch (type) {
                        case 'street_number':
                            this.addressComponentValues.street_number = addressComponent.long_name;
                            break;
                        case 'route':
                            this.addressComponentValues.route = addressComponent.long_name;
                            break;
                        case 'locality':
                            this.addressComponentValues.locality = addressComponent.long_name;
                            break;
                        case 'postal_town':
                            this.addressComponentValues.postal_town = addressComponent.long_name;
                            break;
                        case 'administrative_area_level_1':
                            this.addressComponentValues.administrative_area_level_1 = addressComponent.short_name;
                            break;
                        case 'administrative_area_level_2':
                            this.addressComponentValues.administrative_area_level_2 = addressComponent.short_name;
                            break;
                        case 'postal_code':
                            this.addressComponentValues.postal_code = addressComponent.long_name;
                            break;
                        case 'postal_code_prefix':
                            this.addressComponentValues.postal_code_prefix = addressComponent.long_name;
                            break;
                        case 'country':
                            this.addressComponentValues.country = addressComponent.long_name;
                            break;
                    }
                });
            });

            // Assign values using fallback logic
            const streetNumber = this.addressComponentValues.street_number || '';
            const route = this.addressComponentValues.route || '';
            this.addressData.shippingStreet = [streetNumber, route].filter(Boolean).join(' ').trim();

            this.addressData.shippingCity = this.addressComponentValues.locality
                || this.addressComponentValues.postal_town
                || this.addressComponentValues.administrative_area_level_2
                || '';

            this.addressData.shippingState = this.addressComponentValues.administrative_area_level_1 || '';

            this.addressData.shippingZip = this.addressComponentValues.postal_code
                || this.addressComponentValues.postal_code_prefix
                || '';
            this.addressData.shippingCountry = `${this.addressComponentValues.country}`;
        });

        this.mapsIsInitialized = true;
    }

    @Emit('payment-token-ready')
    tokenReady() {
        // When the token is ready we need set all the auto pay options to be mapped to it to mark it as enabled. 
        // If its a new card we won't have the store ID so just pass in the constant NEW-CARD-ACCOUNT and the backend code will know to take the new id and set it there. 
        if (this.autoPayOptions != null) {
            if (this.paymentContainerToken.paymentType == PaymentTypeEnum.creditCard || this.paymentContainerToken.paymentType == PaymentTypeEnum.ach) {

                var externalPaymentMethodId = this.paymentContainerToken.paymentType == PaymentTypeEnum.creditCard
                    ? (this.paymentContainerToken.creditCardTokenData.storedCardId || "NEW-CARD-ACCOUNT")
                    : (this.paymentContainerToken.achTokenData.storedBankAccountId || "NEW-CARD-ACCOUNT");

                this.autoPayOptions.forEach(x => {
                    if (x.enabled) {
                        x.externalPaymentMethodId = externalPaymentMethodId;
                    } else {
                        x.externalPaymentMethodId = '';
                    }
                })

                if (this.paymentContainerToken.paymentType == PaymentTypeEnum.creditCard) {
                    this.paymentContainerToken.creditCardTokenData.autoPayOptionsJson = JSON.stringify(this.autoPayOptions);
                }
                else if (this.paymentContainerToken.paymentType == PaymentTypeEnum.ach) {
                    this.paymentContainerToken.achTokenData.autoPayOptionsJson = JSON.stringify(this.autoPayOptions);
                }
            }
        }

        if (this.addressData != null) {
            this.paymentContainerToken.addressData = this.addressData;
        }

        return this.paymentContainerToken;
    }

    // Borrowed from https://stackoverflow.com/a/2117523/215141
    uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    resetIdempotencyKey() {
        this.paymentContainerToken.idempotencyKey = this.uuidv4();
    }

    created() {
        EventBus.$on('api-payment-error', this.handleApiPaymentError);
        this.loadCurrentUser();
        this.getPaymentLimits();
        EventBus.$on('update-total', this.handleExternalTotal);
    }

    mounted() {
        const self = this;

        this.$nextTick(() => {
            if (!this.mapsIsInitialized) {
                this.setupGoogleAutocomplete();
            }
        });


        // This is here as a fallback mechanism in case the google maps sdk has not been loaded when mounted() is executed
        window.addEventListener('load', () => {
            if (!this.mapsIsInitialized) {
                self.setupGoogleAutocomplete();
            }
        });

        this.selectDefaultPaymentTab();
        this.notifyKoPaymentType(this.paymentType);
        this.isProcessing = false;
        this.hasItemWithDisabledInvoicing = this.processorOptions.hasItemWithDisabledInvoicing;
        this.disabledInvoiceTitle = this.hasItemWithDisabledInvoicing ? 'One or more attendee tickets has invoicing disabled.' : ''
        this.resetIdempotencyKey();

        if (this.processorOptions.enableAutoPay) {
            this.getAutoPayOptions();
        }

        if (this.initialErrorMessage) {
            this.onErrorToggle(true, this.initialErrorMessage)
        }
    }

    selectDefaultPaymentTab() {
        // Default states
        this.creditCardActive = false;
        this.achActive = false;
        this.invoiceActive = false;
        this.paymentPlansActive = false;

        const currentProcessor = noviNs.paymentProcessor;
        const savedCards = this.processorOptions.savedCards || [];
        const savedAchs = this.processorOptions.savedAchs || [];

        const defaultCreditCardExists = this.processorOptions.enableCreditCard &&
            savedCards.some(card => card.default);

        const defaultAchExists = this.processorOptions.enableACH &&
            savedAchs.some(ach => ach.default);

        const savedCardInCurrentProcessor = this.processorOptions.enableCreditCard &&
            savedCards.some(card => card.paymentProcessorModule === currentProcessor);

        const savedAchInCurrentProcessor = this.processorOptions.enableACH &&
            savedAchs.some(ach => ach.paymentProcessorModule === currentProcessor);

        const anySavedCard = this.processorOptions.enableCreditCard && savedCards.length > 0;

        const anySavedAch = this.processorOptions.enableACH && savedAchs.length > 0;

        if (defaultCreditCardExists) {
            this.creditCardActive = true;
        } else if (defaultAchExists) {
            this.achActive = true;
        } else if (savedCardInCurrentProcessor) {
            this.creditCardActive = true;
        } else if (savedAchInCurrentProcessor) {
            this.achActive = true;
        } else if (anySavedCard) {
            this.creditCardActive = true;
        } else if (anySavedAch) {
            this.achActive = true;
        } else if (this.processorOptions.enableCreditCard) {
            this.creditCardActive = true;
        } else if (this.processorOptions.enableACH) {
            this.achActive = true;
        } else if (this.processorOptions.enableInvoice) {
            this.invoiceActive = true;
        } else {
            this.creditCardActive = true;
        }

        // Handle payment type setting on page load
        if (this.achActive) {
            this.toggleTabActivation(PaymentTypeEnum.ach);
            this.loadPaymentPlans(this.eligibleItemsForPaymentPlan);
        }
        else if (this.creditCardActive) {
            this.toggleTabActivation(PaymentTypeEnum.creditCard);
        }
        else if (this.invoiceActive) {
            this.toggleTabActivation(PaymentTypeEnum.invoice);
        }
    }

    loadPaymentPlans(eligibleItems: []) {
        if (this.currentUser == null) {
            return;
        }

        if (eligibleItems.length > 0 && this.eligibleForPaymentPlanAmount > 0) {
            paymentsRepo.getPaymentPlans(eligibleItems, this.total ?? 0, this.eligibleForPaymentPlanAmount, this.currentUser.customerID ?? undefined)
                .then((paymentPlans: PaymentPlan[]) => {
                    this.paymentPlans = paymentPlans;
                })
                .catch((error: any) => {
                    // checking if manual retry exists
                    if (error.manualRetry) {
                        this.errorMessage = error.message;
                    } else {
                        this.errorMessage = error.data?.message || "Error loading payment plans";
                    }
                })
        }
    }

    onGuestNameUpdated(name: string) {
        if (name) {
            this.guestName = name;
            let creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
            if (creditCardContainer) {
                creditCardContainer.onGuestDataUpdated(name, true);
            }
            let achContainer = <AchContainerClass>this.$refs.achContainer;
            if (achContainer) {
                achContainer.onGuestDataUpdated(name, true);
            }
        }
    }

    onGuestCompanyUpdated(company: string) {
        if (company) {
            this.guestCompany = company;
            let creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
            if (creditCardContainer) {
                creditCardContainer.onGuestDataUpdated(company, false);
            }
            let achContainer = <AchContainerClass>this.$refs.achContainer;
            if (achContainer) {
                achContainer.onGuestDataUpdated(company, false);
            }
        }
    }

    getAutoPayOptions() {
        if (this.autoPayOptionsPulled) {
            return;
        }

        var type = this.processorOptions.isPayingSubscriptionInvoice ? 'subscription' : 'membership';

        paymentsRepo.getAutoPayOptions(type).then((result: { items: AutoPayOption[] }) => {
            this.autoPayOptions = result.items;
            this.autoPayOptionsPulled = true;

            // If auto pay is required for this member then auto enable the checkbox
            result.items.forEach(x => {
                x.enabled = x.forceAutoPay || x.enabled;
            });
        }).catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => { });
    }

    handleExternalTotal(total: any) {
        // Some consuming pages emit totals as strings (ex: Knockout toFixed(2)),
        // and during typing the value can briefly be non-numeric.
        const coerced = typeof total === 'string' ? parseFloat(total) : Number(total);
        this.total = isNaN(coerced) ? (this.total ?? 0) : coerced; // triggers @Watch('total')
    }

    getPaymentLimits() {
        var self = this;

        paymentsRepo.getPaymentLimits()
            .then((result: { limits: PaymentLimits }) => {

                // Use result.limits if it exists, else fallback to result itself
                const serverLimits = result.limits ?? result;

                // If the API returns 0 or null for either max value, we want to fall back to the theoretical max so we don't prevent a checkout
                // The backend validation will still occur and verify that the total is within the allowed payment limits
                const safeMax = (value?: number) =>
                    value == null || value === 0 ? THEORETICAL_MAX_PAYMENT : value;

                const safeLimits = {
                    tenantNameAbbreviated: serverLimits.tenantNameAbbreviated ?? '',
                    achMax: safeMax(serverLimits.achMax),
                    achMin: serverLimits.achMin ?? 0,
                    ccMax: safeMax(serverLimits.ccMax),
                    ccMin: serverLimits.ccMin ?? 0
                };
                self.paymentLimits = createPaymentLimits(safeLimits);
            })
            .catch((error: any) => {
                console.error("Failed to fetch payment limits:", error);

                // Log specific details if it is an AxiosError
                if (error?.response) {
                    console.error("Response status:", error.response.status);
                    console.error("Response data:", error.response.data);
                }
            });
    }

    private loadCurrentUser() {
        customerRepo.getCurrentUser()
            .then(user => {
                Object.assign(this.currentUser, user);
            })
            .catch(error => {
                console.error("Unable to load current user.", error);
            })
            .finally(() => {
                this.userLoaded = true;
            });
    }

    private evaluatePaymentLimits(): void {

        // Reset state first
        this.hasPaymentLimitViolation = false;
        this.paymentLimitMessage = '';
        this.adminPaymentLimitMessage = '';

        // Exit early in these scenarios
        if (!this.paymentLimits || this.total == undefined) {
            return;
        }

        const limits = this.paymentLimits;

        if (this.achActive) {

            // Check for current total over ACH max
            if (limits.achMax != undefined && this.total != null && this.total > limits.achMax) {
                this.hasPaymentLimitViolation = true;
                this.paymentLimitMessage = PaymentLimitsMessages.achMax(limits.tenantNameAbbreviated, limits.achMax);
                this.adminPaymentLimitMessage = AdminPaymentLimitsMessages.achMax(limits.achMax);
                return;
            }
        }

        if (this.creditCardActive) {

            // Check for current total over credit card max
            if (limits.ccMax != undefined && this.total != null && this.total > limits.ccMax) {
                this.hasPaymentLimitViolation = true;
                this.paymentLimitMessage = PaymentLimitsMessages.ccMax(limits.tenantNameAbbreviated, limits.ccMax);
                this.adminPaymentLimitMessage = AdminPaymentLimitsMessages.ccMax(limits.ccMax);
                return;
            }
        }

        // The total wasn't over the allowed limit, clear the error
        this.onErrorToggle(false, '');
    }

    onEcommerceDeliveryAddressStateUpdated(value: boolean): void {
        this.ecommerceDeliveryAddressProvidedOrRequired = !!value;
    }
}